import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Eye, EyeOff, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EvolutionQRPanel } from '@/pages/SettingsPage';

const PROVIDERS = [
  { id: 'gupshup', title: 'Gupshup', desc: 'Enterprise grade messaging con WABA oficial' },
  { id: 'twilio', title: 'Twilio', desc: 'Plataforma global para mensajería escalable' },
  { id: 'evolution', title: 'Evolution API', desc: 'Open source vía QR. Auto-hospedado.' },
];

export const ProveedorTab = () => {
  const [provider, setProvider] = useState('gupshup');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gupshup, setGupshup] = useState({ api_key: '', app_name: '', source_phone: '', configured: false });
  const [twilio, setTwilio] = useState({ account_sid: '', auth_token: '', twilio_phone: '', configured: false });

  useEffect(() => {
    const load = async () => {
      try {
        const [g, t, status] = await Promise.all([
          api.get('/whatsapp/gupshup-config'),
          api.get('/whatsapp/twilio-config').catch(() => ({ data: {} })),
          api.get('/whatsapp/status').catch(() => ({ data: {} })),
        ]);
        setGupshup({
          api_key: '',
          app_name: g.data?.app_name || '',
          source_phone: g.data?.source_phone || '',
          configured: Boolean(g.data?.configured),
          api_key_masked: g.data?.api_key_masked || '',
        });
        setTwilio({
          account_sid: t.data?.account_sid || '',
          auth_token: '',
          twilio_phone: t.data?.twilio_phone || t.data?.phone || '',
          configured: Boolean(t.data?.configured),
        });
        // Si el tenant ya tenía mode=qr, pre-seleccionar el card Evolution.
        const currentMode = String(status.data?.mode || '').toLowerCase();
        if (currentMode === 'qr') setProvider('evolution');
        else if (currentMode === 'twilio') setProvider('twilio');
        else if (currentMode === 'gupshup' || g.data?.configured) setProvider('gupshup');
      } catch {
        toast.error('No se pudo cargar la configuración de proveedor');
      }
    };
    load();
  }, []);

  const saveProvider = async () => {
    setSaving(true);
    try {
      if (provider === 'gupshup') {
        if (!gupshup.app_name || !gupshup.source_phone) throw new Error('App Name y Source Number son requeridos');
        await api.post('/whatsapp/gupshup-config', {
          api_key: gupshup.api_key,
          app_name: gupshup.app_name,
          source_phone: gupshup.source_phone,
        });
        await api.post('/whatsapp/set-mode', { mode: 'gupshup' });
        setGupshup((p) => ({ ...p, configured: true, api_key: '' }));
      } else if (provider === 'twilio') {
        if (!twilio.account_sid || !twilio.twilio_phone) throw new Error('Account SID y número Twilio son requeridos');
        await api.post('/whatsapp/twilio-config', {
          account_sid: twilio.account_sid,
          auth_token: twilio.auth_token,
          phone: twilio.twilio_phone,
        });
        await api.post('/whatsapp/set-mode', { mode: 'twilio' });
        setTwilio((p) => ({ ...p, configured: true, auth_token: '' }));
      } else if (provider === 'evolution' || provider === 'qr') {
        // Persiste mode=qr (Evolution) en whatsapp_config. El QR se obtiene
        // del panel <EvolutionQRPanel /> que se renderiza debajo.
        await api.post('/whatsapp/set-mode', { mode: 'qr' });
      }
      toast.success('Proveedor guardado y verificado');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'No se pudo guardar el proveedor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-proveedor-card">
      <CardHeader>
        <CardTitle className="text-white text-base" data-testid="whatsapp-proveedor-title">Configura tu proveedor</CardTitle>
        <CardDescription className="text-xs">Selecciona y configura el motor de mensajería para WhatsApp.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-3" data-testid="whatsapp-proveedor-options-grid">
          {PROVIDERS.map((item) => {
            const connected = item.id === 'gupshup' ? gupshup.configured : item.id === 'twilio' ? twilio.configured : false;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setProvider(item.id)}
                className={`text-left rounded-xl p-3 border transition ${provider === item.id ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-white/10 bg-black/20 hover:border-white/20'}`}
                data-testid={`whatsapp-proveedor-option-${item.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm text-white font-medium">{item.title}</p>
                  {connected && <Badge className="bg-emerald-500/20 text-emerald-300 border-0" data-testid={`whatsapp-proveedor-connected-${item.id}`}>CONECTADO</Badge>}
                </div>
                <p className="text-[11px] text-zinc-400">{item.desc}</p>
              </button>
            );
          })}
        </div>

        {provider === 'gupshup' && (
          <div className="space-y-3 rounded-xl border border-white/10 bg-black/20 p-3" data-testid="whatsapp-proveedor-gupshup-form">
            <p className="text-xs text-white">Credenciales de Gupshup</p>
            <div>
              <Label className="text-xs">API Key</Label>
              <div className="relative">
                <Input type={showSecret ? 'text' : 'password'} value={gupshup.api_key} onChange={(e) => setGupshup((p) => ({ ...p, api_key: e.target.value }))} placeholder={gupshup.api_key_masked || 'Ingresa tu API Key'} className="pr-10" data-testid="whatsapp-proveedor-gupshup-api-key" />
                <button type="button" onClick={() => setShowSecret((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400" data-testid="whatsapp-proveedor-gupshup-api-key-toggle">{showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
            <div>
              <Label className="text-xs">App Name</Label>
              <Input value={gupshup.app_name} onChange={(e) => setGupshup((p) => ({ ...p, app_name: e.target.value }))} data-testid="whatsapp-proveedor-gupshup-app-name" />
            </div>
            <div>
              <Label className="text-xs">Source Number</Label>
              <Input value={gupshup.source_phone} onChange={(e) => setGupshup((p) => ({ ...p, source_phone: e.target.value }))} data-testid="whatsapp-proveedor-gupshup-source-number" />
            </div>
          </div>
        )}

        {provider === 'twilio' && (
          <div className="space-y-3 rounded-xl border border-white/10 bg-black/20 p-3" data-testid="whatsapp-proveedor-twilio-form">
            <p className="text-xs text-white">Credenciales de Twilio</p>
            <div><Label className="text-xs">Account SID</Label><Input value={twilio.account_sid} onChange={(e) => setTwilio((p) => ({ ...p, account_sid: e.target.value }))} data-testid="whatsapp-proveedor-twilio-account-sid" /></div>
            <div><Label className="text-xs">Auth Token</Label><Input type={showSecret ? 'text' : 'password'} value={twilio.auth_token} onChange={(e) => setTwilio((p) => ({ ...p, auth_token: e.target.value }))} data-testid="whatsapp-proveedor-twilio-auth-token" /></div>
            <div><Label className="text-xs">Número Twilio WhatsApp</Label><Input value={twilio.twilio_phone} onChange={(e) => setTwilio((p) => ({ ...p, twilio_phone: e.target.value }))} data-testid="whatsapp-proveedor-twilio-phone" /></div>
          </div>
        )}

        {provider === 'evolution' && (
          <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="whatsapp-proveedor-evolution-panel">
            <p className="text-xs text-zinc-300 mb-2 flex items-center gap-1">
              <Info className="w-3.5 h-3.5 text-emerald-400" />
              Tras guardar, escanea el QR para vincular tu WhatsApp.
            </p>
            <EvolutionQRPanel />
          </div>
        )}

        <div className="flex items-center justify-end">
          <Button onClick={saveProvider} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="whatsapp-proveedor-save-button">
            {saving ? 'Guardando...' : 'Guardar y verificar conexión'}
          </Button>
        </div>

        <div className="text-[11px] text-zinc-400 border border-white/10 rounded-xl p-3" data-testid="whatsapp-proveedor-footer-status">
          <p className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />Latencia del servidor y verificación operativa visibles tras conexión.</p>
        </div>
      </CardContent>
    </Card>
  );
};
